import { Host, JSONSafe, Task } from "@/types";
import { ComponentRef, useCallback, useRef, useState } from "react";

import logger from "@/lib/logger";
import { cn, okstatus } from "@/lib/utils";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Checkbox } from "@/components/ui/checkbox";

export const DeleteAppDialog = ({
  id,
  onDelete,
  host,
  children,
  disabled
}: {
  id: string;
  onDelete?: () => void;
  host?: Pick<Host, "name" | "id"> | null;
  children?: (onDelete: () => void) => React.ReactNode;
  disabled?: boolean;
}) => {
  const [removeFromHost, setRemoveFromHost] = useState(false);
  const [deleteApp, setDeleteApp] = useState(false);

  const deleteRef = useRef<ComponentRef<typeof ConfirmDialog>>(null);

  const handleDelete = useCallback(async () => {
    await fetch(`/api/app/${id}`, {
      method: "DELETE"
    }).then(okstatus);

    onDelete && onDelete();
  }, [id, onDelete]);

  const handleRemoveFromHost = useCallback(
    (deleteApp: boolean) => async () => {
      try {
        const response = await fetch(`/api/app/${id}/remove`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            deleteApp: !!deleteApp
          })
        })
          .then(okstatus)
          .then(res => res.json() as Promise<JSONSafe<Task>>);
        logger.info("Task created", response);
        onDelete && onDelete();
      } catch (error) {
        logger.warn(error);
      }
    },
    [id, onDelete]
  );

  const handleOpen = useCallback(() => {
    deleteRef.current?.open();
  }, []);

  return (
    <>
      <ConfirmDialog
        ref={deleteRef}
        onContinue={
          removeFromHost
            ? handleRemoveFromHost(deleteApp)
            : deleteApp && !removeFromHost
              ? handleDelete
              : undefined
        }
        title="Delete app"
        className="flex flex-col gap-4"
      >
        <div className="text-md font-bold">Are you sure you want to delete this app?</div>

        <label
          className={cn("flex flex-row gap-2 items-start text-left", !host && "opacity-50")}
        >
          <Checkbox
            checked={removeFromHost}
            onCheckedChange={checked => setRemoveFromHost(!!checked)}
            disabled={!host}
          />
          Remove app from host {host?.name} {host ? "" : "(not available: no host selected)"}
        </label>

        <label className="flex flex-row gap-2 items-center">
          <Checkbox checked={deleteApp} onCheckedChange={checked => setDeleteApp(!!checked)} />
          Delete local app record
        </label>

        <div className="text-muted-foreground text-sm line-clamp-2 h-12">
          {deleteApp && !removeFromHost
            ? "We will only delete local app record. The app will continue to run."
            : deleteApp && removeFromHost
              ? "We will remove the app from the host and delete local app record."
              : !deleteApp && removeFromHost
                ? "We will remove the app from the host. You can run app on another host."
                : "Please select an option to continue."}
        </div>
      </ConfirmDialog>

      {children ? (
        // eslint-disable-next-line react-hooks/refs
        children(handleOpen)
      ) : (
        <DropdownMenuItem
          disabled={disabled}
          onClick={e => {
            e.preventDefault();
            deleteRef.current?.open();
          }}
        >
          Delete
        </DropdownMenuItem>
      )}
    </>
  );
};
